import { useEffect, useState } from 'react'

/**
 * App theme (light / dark / same as device) — the web counterpart of Android's
 * ThemeManager.
 *
 * APP scope, not per business: one localStorage key (`app_theme_mode`, the same
 * name Android uses) that sign-out and business switches never touch. Nothing is
 * written until the merchant picks a mode in Settings; until then
 * DEFAULT_THEME_MODE applies.
 *
 * The resolved theme is published as `data-theme="light|dark"` on <html>; the
 * colour tokens (components/theme.ts) key off that attribute. The same logic runs
 * as an inline script before first paint (THEME_BOOT_SCRIPT, injected by
 * server/components/HTML.tsx) so a dark-mode merchant never sees a white flash.
 */

export type ThemeMode = 'light' | 'dark' | 'system'
type ResolvedTheme = 'light' | 'dark'

const THEME_STORAGE_KEY = 'app_theme_mode'
const THEME_ATTRIBUTE = 'data-theme'

// Same as Android: follow the device until the merchant picks a mode in Settings.
const DEFAULT_THEME_MODE: ThemeMode = 'system'

export const THEME_MODES: ThemeMode[] = ['light', 'dark', 'system']

// Mixpanel values (Android ThemeMode.analyticsName): SYSTEM reports as "device"
export const themeAnalyticsName = (mode: ThemeMode) => (mode === 'system' ? 'device' : mode)

const DARK_QUERY = '(prefers-color-scheme: dark)'

type Listener = () => void
const listeners = new Set<Listener>()

const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

const isThemeMode = (value: unknown): value is ThemeMode =>
  value === 'light' || value === 'dark' || value === 'system'

const getThemeMode = (): ThemeMode => {
  try {
    const stored = storage()?.getItem(THEME_STORAGE_KEY)
    return isThemeMode(stored) ? stored : DEFAULT_THEME_MODE
  } catch {
    return DEFAULT_THEME_MODE
  }
}

const deviceIsDark = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(DARK_QUERY).matches

const resolveTheme = (mode: ThemeMode): ResolvedTheme =>
  mode === 'system' ? (deviceIsDark() ? 'dark' : 'light') : mode

// Theming is for the desktop merchant app only: webviews, staff links and the
// phone pages share this bundle but keep their fixed light look
const themingEnabled = (): boolean =>
  typeof document !== 'undefined' &&
  document.documentElement.getAttribute('data-theme-scope') === 'app'

const applyTheme = (): ResolvedTheme => {
  const resolved = themingEnabled() ? resolveTheme(getThemeMode()) : 'light'
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute(THEME_ATTRIBUTE, resolved)
  }
  return resolved
}

const notify = () => {
  applyTheme()
  listeners.forEach((listener) => listener())
}

const setThemeMode = (mode: ThemeMode): void => {
  try {
    storage()?.setItem(THEME_STORAGE_KEY, mode)
  } catch {
    // Storage blocked: the choice still applies for this page view
  }
  notify()
}

let watching = false
// Follow device changes ("Same as device") and picks made in another tab
const watchExternalChanges = () => {
  if (watching || typeof window === 'undefined') {
    return
  }
  watching = true
  if (typeof window.matchMedia === 'function') {
    const query = window.matchMedia(DARK_QUERY)
    if (query.addEventListener) {
      query.addEventListener('change', notify)
    } else if (query.addListener) {
      query.addListener(notify)
    }
  }
  window.addEventListener('storage', (event) => {
    if (event.key === THEME_STORAGE_KEY) {
      notify()
    }
  })
}

interface IAppTheme {
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
}

export const useAppTheme = (): IAppTheme => {
  const [, setVersion] = useState(0)

  useEffect(() => {
    watchExternalChanges()
    const listener = () => setVersion((version) => version + 1)
    listeners.add(listener)
    // The server cannot know the stored choice; sync once on mount
    listener()
    return () => {
      listeners.delete(listener)
    }
  }, [])

  return { mode: getThemeMode(), setMode: setThemeMode }
}

// Runs in <head> before first paint. Kept dependency-free and in step with
// getThemeMode / resolveTheme / applyTheme above.
export const THEME_BOOT_SCRIPT = `(function(){try{var d=document.documentElement;if(d.getAttribute('data-theme-scope')!=='app'){d.setAttribute('${THEME_ATTRIBUTE}','light');return}var m=null;try{m=localStorage.getItem('${THEME_STORAGE_KEY}')}catch(e){}if(m!=='light'&&m!=='dark'&&m!=='system'){m='${DEFAULT_THEME_MODE}'}var k=m==='system'?(window.matchMedia&&window.matchMedia('${DARK_QUERY}').matches?'dark':'light'):m;d.setAttribute('${THEME_ATTRIBUTE}',k)}catch(e){}})()`
